import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import { cn } from './cn';

const SIZES = {
  sm: 'max-w-[400px]',
  md: 'max-w-[520px]',
  lg: 'max-w-[680px]',
  xl: 'max-w-[840px]',
};

/**
 * Drawer — side panel that slides from the right. Preferred over Modal for
 * read/edit flows that should preserve list context (e.g. row click → peek).
 *
 *   <Drawer isOpen={...} close={...} title="Acme Corp" size="md">
 *     <Drawer.Body>...</Drawer.Body>
 *     <Drawer.Footer>...</Drawer.Footer>
 *   </Drawer>
 */
export function Drawer({ isOpen, close, title, subtitle, headerActions, size = 'md', children }) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === 'Escape' && close?.();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={title}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={close}
            className="absolute inset-0 bg-ink-950/40 backdrop-blur-[2px]"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'absolute right-0 top-0 flex h-full w-full flex-col',
              'bg-surface shadow-e3 ring-1 ring-line/60',
              SIZES[size],
            )}
          >
            <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
              <div className="min-w-0">
                {title && <h2 className="text-h1 text-fg truncate">{title}</h2>}
                {subtitle && <p className="mt-0.5 text-caption text-fg-muted">{subtitle}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {headerActions}
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-ink-100 hover:text-fg dark:hover:bg-ink-700"
                >
                  <FiX size={18} />
                </button>
              </div>
            </header>
            {children}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

Drawer.Body = function DrawerBody({ className, children }) {
  return <div className={cn('flex-1 overflow-y-auto px-5 py-4', className)}>{children}</div>;
};

Drawer.Footer = function DrawerFooter({ className, children }) {
  return (
    <footer
      className={cn(
        'flex items-center justify-end gap-2 border-t border-line bg-surface-muted px-5 py-3',
        className,
      )}
    >
      {children}
    </footer>
  );
};

export default Drawer;
